import { dateFormat } from '../hepler/functions';

const stableFipsList = [];

export const analyzeFipAndAddToStableList = (fipId, discoveredAccounts, linkedAccounts) => {
    // Skip if already analyzed
    if (stableFipsList.includes(fipId)) {
        return;
    }
    
    const fipLinkedAccounts = linkedAccounts.filter(linked => linked.fipId === fipId);
    
    // No linked accounts → stable
    if (fipLinkedAccounts.length === 0) {
        stableFipsList.push(fipId);
        return;
    }
    
    // Count how many linked accounts have matching discovered accounts with same accRefNumber
    let matchCount = 0;
    
    fipLinkedAccounts.forEach(linked => {
        const hasMatchingDiscovered = discoveredAccounts.some(discovered => 
            discovered.FIType === linked.FIType &&
            discovered.accType === linked.accType &&
            discovered.maskedAccNumber === linked.maskedAccNumber &&
            discovered.accRefNumber === linked.accRefNumber
        );
        
        if (hasMatchingDiscovered) {
            matchCount++;
        }
    });
    // If all linked accounts have matching discovered accounts with same accRefNumber, mark as stable
    if (matchCount === fipLinkedAccounts.length) {
        if (!stableFipsList.includes(fipId)) {
            stableFipsList.push(fipId);
        }
    }
};

export const isAccountLinked = (discoveredAccount, linkedAccounts, fipId = null) => {
	const { FIType, accType, maskedAccNumber, accRefNumber } = discoveredAccount;
	if (stableFipsList.includes(fipId)) {
		return linkedAccounts.some(
			(linkedAccount) => linkedAccount.accRefNumber === accRefNumber
		);
	} else {
		return linkedAccounts.some(
			(linkedAccount) =>
				linkedAccount.FIType === FIType &&
				linkedAccount.accType === accType &&
				linkedAccount.maskedAccNumber === maskedAccNumber
		);
	}
};

export const getUniqueKey = (account, fipId) => {
	return stableFipsList.includes(fipId) && account.accRefNumber
		? account.accRefNumber 
		: `${fipId}_${account.maskedAccNumber}_${account.FIType}_${account.accType}`;
};

export const showSelectButton = (consents, consentOptions) => {
	// the button will always be true all LSP 
	if (global.institutionType == 'LSP') {
		return true;
	}
	
	for (const consent of consents) {
		let matchFound = false;
		const purposeCode = consent?.Purpose?.code;
		
		for (const option of consentOptions) {
			// Convert both to strings for comparison 
			if (String(option["Purpose.code"]) === String(purposeCode)) {
				matchFound = true;
				if (option.checkbox) {
					return true;
				}
				break;
			}
		}	
		if (!matchFound) {
			return true;
		}
	}
	// if all contain matched purpose and checkbox false then we will return false 		
	return false;
}

//Filtering Accounts based on FIType and AccType
export const filterAccountsByRestrictions = (accounts) => {
	const accTypes = Array.isArray(global?.accountRestrictions?.accType)
		? global?.accountRestrictions?.accType.map((type) => type.trim())
		: global?.accountRestrictions?.accType
		? [global?.accountRestrictions?.accType.trim()]
		: [];

	const fiTypes = Array.isArray(global?.accountRestrictions?.FIType)
		? global?.accountRestrictions?.FIType.map((type) => type.trim())
		: global?.accountRestrictions?.FIType
		? [global?.accountRestrictions?.FIType.trim()]
		: [];
   
	return accounts.filter((acc) => 
		global?.accountRestrictions?.FIType 
			? accTypes.includes(acc.accType) && fiTypes.includes(acc.FIType)
			: accTypes.includes(acc.accType)
	);
};

export const fipExclusionFilter = (accounts) => {
	return accounts.filter((account) => {
		const fipId = account.fipId;
		return !JSON.parse(global?.sdkOptions?.fipExclusionFromJourney).includes(
			fipId
		);
	});
};

export const removeFipIdDots = (key) => {
	return key.replace(/\./g, '');
};

export const getConsentParameterValue = (paramPath, consentData) => {
	if (!paramPath || !consentData) {
		return null;
	}

	try {
		// Navigate to consentHandleDetails if it exists
		const consentDetails = consentData.consentHandleDetails || consentData;
		// Split the parameter path by dots to handle nested properties
		const pathParts = paramPath.split('.');
		let value = consentDetails;

		// Navigate through the nested object
		for (const part of pathParts) {
			if (value && typeof value === 'object' && part in value) {
				value = value[part];
			} else {
				return null; // Path doesn't exist
			}
		}

		// Check if the value is a date string and format it
		if (typeof value === 'string' && isDateString(value)) {
			return dateFormat(value, false); // Using existing dateFormat function
		}

		return value;
	} catch (error) {
		return null;
	}
};

const isDateString = (str) => {
	if (!str || typeof str !== 'string') return false;
	
	// Check for ISO date format pattern
	const isoDatePattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?([+-]\d{2}:\d{2}|Z)$/;
	
	if (isoDatePattern.test(str)) {
		const date = new Date(str);
		return !isNaN(date.getTime());
	}
	
	return false;
};


export const compareConsentOptions = (consentDetails, consentOptions, t) => {
	if (!consentOptions || !consentDetails) {
		return consentDetails?.Purpose?.text || '';
	}
	// Check if ANY option has frequency properties
	const hasFrequencyOptions = consentOptions.some(option =>
		option?.['Frequency.unit'] || option?.['Frequency.value']
	);
	// If no frequency options exist, return default text
	if (!hasFrequencyOptions) {
		return consentDetails?.Purpose?.text || '';
	}

	let matchedOption = null;
	consentOptions.forEach((option) => {
		const purposeMatches = String(option?.['Purpose.code']) === String(consentDetails?.Purpose?.code);

		if (!option?.['Frequency.unit']) {
			if (purposeMatches) {
				matchedOption = option;
			}
		} else {
			const frequencyUnitMatches = option?.['Frequency.unit'] === consentDetails?.Frequency?.unit;
			if (purposeMatches && frequencyUnitMatches) {
				matchedOption = option;
			}
		}
	});

	if (matchedOption) {
		if (matchedOption?.['Purpose.code'] === 103) {
			return t('paymentConsent.titles.purposeText103');
		} else if (matchedOption?.['Purpose.code'] === 104 && matchedOption?.['Frequency.unit'] === 'MONTH') {
			return t('paymentConsent.titles.104_For_Monitoring_Of_Account');
		} else if (matchedOption?.['Purpose.code'] === 104 && matchedOption?.['Frequency.unit'] === 'DAY') {
			return t('paymentConsent.titles.104_For_Collection');
		}
	}
	return consentDetails?.Purpose?.text || '';
};

/**
 * Filters linked accounts to only include those that match consent accounts
 * @param {Array} consentAccounts - Array of accounts from consent details
 * @param {Array} linkedAccounts - Array of all user linked accounts
 * @returns {Array} Filtered array of linked accounts that match consent accounts
 */
export const filterMatchingAccounts = (consentAccounts, linkedAccounts) => {
	if (!consentAccounts || !linkedAccounts) return [];

	return linkedAccounts.filter((linkedAcc) => {
		return consentAccounts.some((consentAcc) => {
			const accTypeMatch =
				( linkedAcc.accType)?.toLowerCase() ===
				(consentAcc.accType)?.toLowerCase();
			const fiTypeMatch =
				linkedAcc.FIType?.toLowerCase() === consentAcc.FIType?.toLowerCase();
			const maskedAccMatch =
				linkedAcc.maskedAccNumber === consentAcc.maskedAccNumber;
			const linkRefMatch =
				linkedAcc.linkRefNumber === consentAcc.linkRefNumber;

			return accTypeMatch && fiTypeMatch && maskedAccMatch && linkRefMatch;
		});
	});
};
